import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  applyCartCoupon,
  fetchCart,
  removeCartCoupon,
  removeCartItem,
  updateCartQuantity,
} from "./commerceApi";
import { moneyLabel } from "./payloadDisplay";
import "./CartPage.css";

/**
 * A-008 — Cart UI renders only cartPayload fields. No pricing math on the client.
 */
function CartPage() {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [couponInput, setCouponInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchCart();
      setCart(payload);
      if (payload.coupon && payload.coupon.code) {
        setCouponInput(payload.coupon.code);
      }
    } catch (e) {
      setError(e.message || "Unable to load cart.");
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const payload = await fn();
      setCart(payload);
    } catch (e) {
      setError(e.message || "Cart update failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="commerce-cart commerce-cart--loading">
        <p>Loading cart…</p>
      </div>
    );
  }

  const items = (cart && cart.items) || [];
  const summary = (cart && cart.summary) || {};
  const warnings = (cart && cart.warnings) || [];
  const messages = (cart && cart.messages) || [];

  return (
    <div className="commerce-cart">
      <Helmet>
        <title>Cart | Pentecost+</title>
      </Helmet>

      <div className="commerce-cart__inner">
        <p className="commerce-cart__crumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden="true"> / </span>
          <span>Cart</span>
        </p>

        <h1 className="commerce-cart__title">Cart</h1>

        {error ? <p className="commerce-cart__error">{error}</p> : null}

        {warnings.map((w, i) => (
          <p key={`w-${i}`} className="commerce-cart__warning">
            {w.message || w.code}
          </p>
        ))}
        {messages.map((m, i) => (
          <p key={`m-${i}`} className="commerce-cart__message">
            {typeof m === "string" ? m : m.message}
          </p>
        ))}

        {!items.length ? (
          <div className="commerce-cart__empty">
            <p>Your cart is empty.</p>
            <Link className="btn btn-danger" to="/shop">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="commerce-cart__layout">
            <ul className="commerce-cart__lines">
              {items.map((item) => {
                const product = item.product || {};
                const pricing = item.pricing || {};
                const entity = item.entity || {};
                const path = product.path || "#";
                return (
                  <li key={item.id} className="commerce-cart__line">
                    {product.primary_image ? (
                      <Link to={path} className="commerce-cart__thumb">
                        <img src={product.primary_image} alt="" />
                      </Link>
                    ) : (
                      <div className="commerce-cart__thumb commerce-cart__thumb--empty" />
                    )}
                    <div className="commerce-cart__line-body">
                      <Link to={path} className="commerce-cart__line-name">
                        {product.name}
                      </Link>
                      {entity.name ? (
                        <p className="commerce-cart__line-entity">{entity.name}</p>
                      ) : null}
                      <p className="commerce-cart__line-type">{item.type}</p>
                      <p className="commerce-cart__line-price">
                        {pricing.label
                          ? pricing.label
                          : moneyLabel(pricing, "unit_price")}
                      </p>
                      {item.type === "physical" ? (
                        <div className="commerce-cart__qty">
                          <button
                            type="button"
                            disabled={busy || item.quantity <= 1}
                            onClick={() =>
                              run(() =>
                                updateCartQuantity(item.id, item.quantity - 1)
                              )
                            }
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              run(() =>
                                updateCartQuantity(item.id, item.quantity + 1)
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <p className="commerce-cart__qty-static">Qty {item.quantity}</p>
                      )}
                      <button
                        type="button"
                        className="commerce-cart__remove"
                        disabled={busy}
                        onClick={() => run(() => removeCartItem(item.id))}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="commerce-cart__line-total">
                      {moneyLabel(pricing, "line_subtotal")}
                    </div>
                  </li>
                );
              })}
            </ul>

            <aside className="commerce-cart__summary">
              <h2>Summary</h2>
              <dl>
                <div>
                  <dt>Subtotal</dt>
                  <dd>{moneyLabel(summary, "subtotal")}</dd>
                </div>
                <div>
                  <dt>Discounts</dt>
                  <dd>
                    {summary.discounts
                      ? `−${moneyLabel(summary, "discounts")}`
                      : moneyLabel(summary, "discounts")}
                  </dd>
                </div>
                <div>
                  <dt>Taxes</dt>
                  <dd>
                    {summary.taxes == null
                      ? "Calculated at checkout"
                      : moneyLabel(summary, "taxes")}
                  </dd>
                </div>
                <div>
                  <dt>Shipping</dt>
                  <dd>
                    {summary.shipping == null
                      ? "Calculated at checkout"
                      : moneyLabel(summary, "shipping")}
                  </dd>
                </div>
                <div className="commerce-cart__summary-total">
                  <dt>Total</dt>
                  <dd>{moneyLabel(summary, "total")}</dd>
                </div>
              </dl>

              <div className="commerce-cart__coupon">
                {cart.coupon && cart.coupon.code ? (
                  <p>
                    Coupon <strong>{cart.coupon.code}</strong> applied.{" "}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => removeCartCoupon())}
                    >
                      Remove
                    </button>
                  </p>
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(() => applyCartCoupon(couponInput.trim()));
                    }}
                  >
                    <label htmlFor="cart-coupon">Coupon</label>
                    <div className="commerce-cart__coupon-row">
                      <input
                        id="cart-coupon"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        disabled={busy}
                        autoComplete="off"
                      />
                      <button type="submit" className="btn btn-secondary" disabled={busy}>
                        Apply
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <Link
                className="btn btn-danger commerce-cart__checkout-btn"
                to="/checkout"
              >
                Proceed to checkout
              </Link>
              <p className="commerce-cart__checkout-note">
                Payment collection arrives in a later milestone. Checkout creates
                a pending order only.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartPage;
